/**
 * LinkGrid Component
 * Displays links in a responsive grid layout with search and tag filtering
 */

import { useEffect, useState, useCallback, useMemo } from "react";
import { Link2, Loader2 } from "lucide-react";
import type { Link as ContractLink } from "@link-storage/contracts";
import type { Link, Tag } from "../types/link";
import { useInfiniteLinks } from "../hooks/useInfiniteLinks.js";
import { useSearch } from "../hooks/useSearch";
import { useMetadataPolling } from "../hooks/useMetadataPolling";
import { LinkCard } from "./LinkCard";
import { LinkCardSkeleton } from "./LinkCardSkeleton";
import { SearchInput } from "./SearchInput";
import { TagFilter } from "./TagFilter";
import { Button } from "./ui/button";

/**
 * Convert contract Link to local Link type
 * This is needed because @effect/schema's Schema.Class has complex type inference
 */
const toLocalLink = (link: ContractLink): Link => {
  const linkWithMeta = link as unknown as {
    ogMetadata: Link["ogMetadata"];
    tags: Tag[];
  };
  return {
    id: link.id,
    url: link.url,
    title: link.title,
    createdAt: link.createdAt,
    updatedAt: link.updatedAt,
    ogMetadata: linkWithMeta.ogMetadata,
    tags: linkWithMeta.tags || [],
  };
};

interface LinkGridProps {
  refreshKey?: number;
  onAddClick?: () => void;
}

export function LinkGrid({ refreshKey = 0, onAddClick }: LinkGridProps) {
  const [selectedTags, setSelectedTags] = useState<string[]>([]);

  const {
    links: rawLinks,
    isLoading,
    hasMore,
    sentinelRef,
    reset,
  } = useInfiniteLinks();

  // Reset when refreshKey changes
  useEffect(() => {
    reset();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refreshKey]);

  // Convert contract links to local type
  const convertedLinks = useMemo(
    () => rawLinks.map(toLocalLink),
    [rawLinks]
  );

  // Metadata overrides from polling (merged synchronously with convertedLinks)
  const [metadataOverrides, setMetadataOverrides] = useState<Map<string, Link>>(new Map());

  // Clear overrides on reset (when rawLinks change from empty)
  useEffect(() => {
    if (rawLinks.length === 0) {
      setMetadataOverrides(new Map());
    }
  }, [rawLinks.length]);

  const localLinks = useMemo(
    () => convertedLinks.map((link) => metadataOverrides.get(link.id) ?? link),
    [convertedLinks, metadataOverrides]
  );

  // Handle metadata updates from polling
  const handleMetadataUpdate = useCallback((updatedLink: Link) => {
    setMetadataOverrides((prev) => {
      const next = new Map(prev);
      next.set(updatedLink.id, updatedLink);
      return next;
    });
  }, []);

  // Poll for pending metadata updates
  useMetadataPolling(localLinks, handleMetadataUpdate);

  // Handle link delete/edit — refetch from page 1
  const handleLinkChange = useCallback(() => {
    reset();
  }, [reset]);

  // Filter links by selected tags
  const tagFilteredLinks = useMemo(() => {
    if (selectedTags.length === 0) return localLinks;
    return localLinks.filter((link) =>
      selectedTags.every((tag) =>
        link.tags?.some((t) => t.name === tag)
      )
    );
  }, [localLinks, selectedTags]);

  const {
    query,
    setQuery,
    filteredItems,
    clearSearch,
    hasSearchQuery,
    hasNoResults,
  } = useSearch(tagFilteredLinks);

  // Initial loading state
  if (localLinks.length === 0 && isLoading) {
    return (
      <div className="space-y-6">
        <SearchInput value="" onChange={() => {}} onClear={() => {}} />
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
          {[...Array(8)].map((_, i) => (
            <LinkCardSkeleton key={i} />
          ))}
        </div>
      </div>
    );
  }

  if (localLinks.length === 0 && !isLoading) {
    return (
      <div className="flex flex-col items-center justify-center py-16 text-center">
        <div className="mb-4 rounded-full bg-muted p-4">
          <Link2 className="h-8 w-8 text-muted-foreground" />
        </div>
        <h3 className="text-lg font-semibold">저장된 링크가 없습니다</h3>
        <p className="mt-1 text-muted-foreground">
          첫 번째 링크를 추가해보세요!
        </p>
        {onAddClick && (
          <Button className="mt-4" onClick={onAddClick}>
            링크 추가하기
          </Button>
        )}
      </div>
    );
  }

  const hasTagFilter = selectedTags.length > 0;
  const hasAnyFilter = hasSearchQuery || hasTagFilter;
  const linkCountLabel = hasMore
    ? `${localLinks.length}+개의 링크`
    : `${localLinks.length}개의 링크`;

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-3">
        <div className="flex items-center justify-between gap-4">
          <div className="flex-1">
            <SearchInput
              value={query}
              onChange={setQuery}
              onClear={clearSearch}
            />
          </div>
          <p className="shrink-0 text-sm text-muted-foreground">
            {hasAnyFilter
              ? `${filteredItems.length}개 검색됨`
              : linkCountLabel}
          </p>
        </div>
        <TagFilter
          selectedTags={selectedTags}
          onTagsChange={setSelectedTags}
        />
      </div>

      {hasNoResults ? (
        <div className="flex flex-col items-center justify-center py-12 text-center">
          <p className="text-lg font-medium">"{query}" 검색 결과가 없습니다</p>
          <p className="mt-1 text-muted-foreground">
            검색어를 변경하거나 새 링크를 추가해보세요
          </p>
          <Button variant="outline" className="mt-4" onClick={clearSearch}>
            검색어 지우기
          </Button>
        </div>
      ) : (
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
          {filteredItems.map((link) => (
            <LinkCard
              key={link.id}
              link={link}
              onDelete={handleLinkChange}
              onEdit={handleLinkChange}
            />
          ))}
        </div>
      )}

      {/* Sentinel div for IntersectionObserver — triggers next page load */}
      <div ref={sentinelRef} />

      {/* Loading more indicator */}
      {localLinks.length > 0 && isLoading && (
        <div className="flex justify-center py-4">
          <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
        </div>
      )}
    </div>
  );
}
